import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../../components/common/Modal";
import Skeleton from "../../../components/common/Skeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";
import {
  CalendarBucket,
  CalendarDueItem,
  fetchDueItems,
} from "../../../services/calendarService";

interface CalendarDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date | null;
  initialBucket?: CalendarBucket;
  availableBuckets?: CalendarBucket[];
}

const fmtCell = (s: string | null | undefined) => (s ? s : "—");

const BUCKET_LABEL: Record<CalendarBucket, string> = {
  due: "Due",
  overdue: "Overdue",
  upcoming: "Upcoming",
  completed: "Completed",
};

const BUCKET_DOT_COLOR: Record<CalendarBucket, string> = {
  due:       "var(--amber-500)",
  overdue:   "var(--red-500)",
  upcoming:  "var(--blue-500)",
  completed: "var(--green-500)",
};

const BUCKET_HEADER_NOTE: Record<CalendarBucket, string> = {
  due:       "Items whose due date is this day.",
  overdue:   "Open items past their due date as of today.",
  upcoming:  "Items falling due within the next 7 days.",
  completed: "Items marked complete on this day.",
};

const BUCKET_DATE_COL_LABEL: Record<CalendarBucket, string> = {
  due:       "Due",
  overdue:   "Due",
  upcoming:  "Due",
  completed: "Completed",
};

const CalendarDayModal: React.FC<CalendarDayModalProps> = ({
  isOpen,
  onClose,
  date,
  initialBucket = "due",
  availableBuckets = ["due"],
}) => {
  const navigate = useNavigate();
  const { formatDate } = useEntitySettings();
  const niceDate = (d: Date) => {
    const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
    return `${weekday}, ${formatDate(d)}`;
  };
  const [activeBucket, setActiveBucket] = useState<CalendarBucket>(initialBucket);
  const [items, setItems] = useState<CalendarDueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) setActiveBucket(initialBucket);
  }, [isOpen, initialBucket]);

  useEffect(() => {
    if (!isOpen || !date) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDueItems(date, activeBucket)
      .then((data) => { if (!cancelled) setItems(data); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load items"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, date, activeBucket]);

  const openItem = (it: CalendarDueItem) => {
    if (!it.route_path) return;
    onClose();
    navigate(it.route_path);
  };

  const renderDateCol = (it: CalendarDueItem) =>
    activeBucket === "completed" ? formatDate(it.completed_date) : formatDate(it.due_date);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        Calendar Items {date ? `— ${niceDate(date)}` : ""}
      </Modal.Header>

      <Modal.Body className="flex flex-col">
        <div className="app-modal-tabs">
          {availableBuckets.map((b) => {
            const isActive = b === activeBucket;
            return (
              <button
                type="button"
                key={b}
                onClick={() => setActiveBucket(b)}
                data-testid={`cal-tab-${b}`}
                className={`app-modal-tab${isActive ? " app-modal-tab--active" : ""}`}
              >
                <span
                  aria-hidden
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: BUCKET_DOT_COLOR[b],
                    flexShrink: 0,
                  }}
                />
                {BUCKET_LABEL[b]}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-muted mb-2 ps-1">{BUCKET_HEADER_NOTE[activeBucket]}</p>

        {error && !loading && (
          <div className="modal-error-banner" role="alert">{error}</div>
        )}

        <div className="table-list flex-1 min-h-0" style={{ display: "flex", flexDirection: "column" }}>
          <div className="table-list-scroll" style={{ flex: "1", minHeight: 0 }}>
            <table className="table-list-table">
              <thead>
                <tr>
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  <th className="table-list-th">Category</th>
                  <th className="table-list-th">Title</th>
                  <th className="table-list-th">Role</th>
                  <th className="table-list-th">Start</th>
                  <th className="table-list-th">{BUCKET_DATE_COL_LABEL[activeBucket]}</th>
                </tr>
              </thead>
              <tbody aria-busy={loading} aria-live="polite">
                {loading && (
                  <Skeleton.TableRows cols={["3xs", "sm", "md", "xs", "xs", "xs"]} />
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="table-list-cell">
                      <NoDataFound
                        title="No Items"
                        message="Nothing in this category for this date."
                      />
                    </td>
                  </tr>
                )}
                {!loading && items.map((it, idx) => (
                  <tr
                    key={`${it.source}-${it.source_id}`}
                    className="table-list-row"
                    onClick={() => openItem(it)}
                    data-testid={`cal-item-${activeBucket}-${it.source}-${it.source_id}`}
                    style={it.route_path ? undefined : { cursor: "default" }}
                  >
                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                    <td className="table-list-cell">
                      <span className="table-list-cell-sub">{it.source_label}</span>
                    </td>
                    <td className="table-list-cell table-list-cell--truncate" title={it.title}>
                      <span className="table-list-cell-title">{it.title}</span>
                      {it.subtitle && (
                        <div className="text-xs text-muted text-truncate" style={{ maxWidth: 420 }}>
                          {it.subtitle}
                        </div>
                      )}
                    </td>
                    <td className="table-list-cell text-muted">{it.role}</td>
                    <td className="table-list-cell text-muted">{formatDate(it.start_date)}</td>
                    <td className="table-list-cell text-muted">{renderDateCol(it)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CalendarDayModal;
